import { useLazyQuery, useQuery } from '@apollo/client'
import { useState, useEffect } from 'react'
import Select, { ActionMeta, components } from "react-select";
import { Flex, Box, Text, Card } from 'rebass/styled-components'
import styled from 'styled-components'
import Modal from 'styled-react-modal'
import { Skeleton } from '../../components/loadingSkeleton/Skeleton'
import { GET_PATIENT_MOBILE_COURSES, GET_PRESET_PLANS, GET_THYROID_PROGRAM_SUMMARY } from '../../graphql/queries'
import { IGetPatientMobileCoursesInput, IGetPatientMobileCoursesOutput } from '../../graphql/types/queries/course'
import { IGetPresetPlanSummaryInput, IGetPresetPlanSummaryOutput, IGetPresetPlansInput, IGetPresetPlansOutput, IPresetPlanSummary } from '../../graphql/types/queries/presetPlans'
import { parseStringify } from '../../utils/StringUtils'
import { ReactSelectStyles } from '../../styles/ReactSelectStyles';
import { RbCircledChecked, RbCircledUnchecked } from '../../components/RadioButton';
import { SpecialModalBackground } from '../../components/Commons';
import PleaseWait from '../../components/PleaseWait';
import { weeksSummary } from './components/weeksSummary';
import { Weeker } from './components/Weeker';
import { DetailsCoursesProgressAip } from './components/DetailsCoursesProgressAip';
import { DetailsProgramProgress } from './components/DetailsProgramProgress';
import { PresetPlan } from './components/PresetPlan';

const Wrapper = styled(Flex)`
  overflow: auto;
  width: 800px;
  padding-right: 20px;
  margin-right: 20px;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

export const HoverableCard = styled(Card)`
  transition:all 0.3s ease;
  flex: initial;
  cursor: ${props => (props.onClick ? 'pointer' : '')};
`

export const ListContainer = styled(Flex)`
  min-width: min(300px, 100vw);
  padding-right: 12px;
  overflow-y: auto;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
  
  &.selected {
    display: none;
  }

  @media screen and (min-width: 68.8125em) {
    width: auto;
    &.selected {
      display: flex;
    }
  }
`;

interface ILastCoursesProps {
  id: string
  isMinimized?: boolean
}

const CourseRowLoading = () => {
  return (
    <HoverableCard mb={2}
      style={{
        opacity: 1,
        boxShadow: '0px 4px 20px rgb(0 0 0 / 10%)'
      }}>
      <Flex style={{ width: '100%', padding: '18px 12px' }} flexDirection='column'>
        <Text style={{ color: 'rgba(52,79,122,0.6)', fontSize: '12px' }}><Skeleton height={18} width={45} /></Text>
        <Text style={{ fontSize: '12px' }}><Skeleton height={21} width={140} /></Text>
      </Flex>
    </HoverableCard>
  )
}

export interface ISummary {
  weekNumber: number;
  complete: boolean;
  notStarted: boolean;
  journeyName: string;
  pct: number;
}

interface IProgram {
  label: string;
  value: 'aipProgram' | string;

}

const Option = (props: any) => {
  return (
    <components.Option {...props}>
      <Flex alignItems={'center'}>
        {props.isSelected ? (
          <RbCircledChecked />
        ) : <RbCircledUnchecked />}

        <Text ml={2}>{props.children}</Text>
      </Flex>
    </components.Option>
  );
};

export const getSections = (secs: IPresetPlanSummary[] | undefined) => {
  if (!secs) return []
  const res = []
  for (let index = 0; index < secs.length; index++) {
    const element = secs[index];
    const progress = element.courses.reduce((a, b) => { return a + (b.progress || 0) }, 0)
    
    res.push({
      id: element.section.id,
      name: element.section.name,
      progress: progress / element.courses.length,
      isComplete: element.courses.filter(x => !x.isComplete).length < 1,
      isStarted: element.courses.filter(x => x.isStarted).length > 0
    })
  }

  return res
}

export const LastCourses: React.FC<ILastCoursesProps> = ({ id, isMinimized }) => {
  const [programs, setPrograms] = useState<IProgram[]>([{
    label: 'AIP Program',
    value: 'aipProgram'
  }])
  const [selectedProgram, setSelectedProgram] = useState<IProgram>()
  const [selectedSection, setSelectedSection] = useState<IPresetPlanSummary | undefined>()
  const [selectedAip, setSelectedAip] = useState<ISummary | undefined>()
  const { data: dataThyroidJourney, loading: loadingThyroidJourney } = useQuery<IGetPatientMobileCoursesOutput, IGetPatientMobileCoursesInput>(GET_PATIENT_MOBILE_COURSES, {
    variables: {
      patientId: id
    },
    onCompleted: e => {
      if (!!isMinimized) return
      if (e && e.providerGetPatientMobileCourses && e.providerGetPatientMobileCourses.byWeek && e.providerGetPatientMobileCourses.byWeek.length > 0) {
        const weeks = weeksSummary(e.providerGetPatientMobileCourses.byWeek)
        setSelectedAip(weeks[0])
      }
    }
  })
  const { data: dataPresetPlans, loading: loadingPresetPlans, error } = useQuery<IGetPresetPlansOutput, IGetPresetPlansInput>(GET_PRESET_PLANS, {
    onCompleted: e => {
      const p: IProgram[] = parseStringify(programs)
      if (e && e.getThyroidPrograms) {
        for (let index = 0; index < e.getThyroidPrograms.length; index++) {
          const element = e.getThyroidPrograms[index];
          p.push({
            label: element.name,
            value: element.id
          })
        }
        setPrograms(p)
      }
    }
  })

  useEffect(() => {
    setSelectedProgram(programs[0])
  }, [])
  
  const [getPlanContent, { data, loading }] = useLazyQuery<IGetPresetPlanSummaryOutput, IGetPresetPlanSummaryInput>(GET_THYROID_PROGRAM_SUMMARY, {
    onCompleted: e => {
      if (!!isMinimized) return
      if (e && e.getThyroidProgramSummaryHealthCoach && e.getThyroidProgramSummaryHealthCoach.length > 0) {
        const dets = e.getThyroidProgramSummaryHealthCoach[0]
        setSelectedSection(dets)
      }
    }
  })

  useEffect(() => {
    if (!selectedProgram) return
    if (selectedProgram.value === 'aipProgram') {
      return
    }

    getPlanContent({
      variables: {
        programId: selectedProgram.value,
        userId: id
      }
    })
  }, [selectedProgram])

  return (
    <Flex flexDirection={'column'} flex={1} width={'100%'} overflow={'hidden'}>
      <Flex mb={4}>
        <Select
          isSearchable={false}
          styles={ReactSelectStyles.programsStyles}
          options={programs}
          value={selectedProgram}
          onChange={(option: IProgram | null, actionMeta: ActionMeta<IProgram>) => {
            console.log({ option })
            if (!option) return
            setSelectedProgram(option || programs[0])
          }}
          components={{ Option }} />
        <Box flex={1}></Box>
      </Flex>
      {!!selectedProgram && selectedProgram.value !== 'aipProgram' && !!loading && (
        <PleaseWait />
      )}
      {!!selectedProgram && selectedProgram.value === 'aipProgram' && !!loadingThyroidJourney && (
        <PleaseWait />
      )}
      {(!selectedProgram) && (!!loading || !!loadingThyroidJourney) && (
        <PleaseWait />
      )}

      {!!selectedProgram && selectedProgram.value !== 'aipProgram' && !loading && (
        <Flex flex={1} width='100%' overflowY={'hidden'}>
          <PresetPlan details={data?.getThyroidProgramSummaryHealthCoach} onSelect={e => setSelectedSection(e)} />
          {!!selectedSection && !isMinimized && (
            <Flex style={{
              flex: 1,
              overflow: 'hidden',
              background: 'white',
              borderRadius: '20px',
              padding: '12px'
            }}>
              <ListContainer flexDirection={'column'} flex={1} width='100%'>
                <DetailsProgramProgress plan={selectedSection} id={id} />
              </ListContainer>
            </Flex>
          )}
        </Flex>
      )}

      {!!selectedProgram && selectedProgram.value === 'aipProgram' && !loadingThyroidJourney && (
        <Flex flex={1} width='100%' overflowY={'hidden'}>

          <Weeker weeks={dataThyroidJourney?.providerGetPatientMobileCourses.byWeek || []} onSelect={setSelectedAip} />
          {!!selectedAip && !isMinimized && (
            <Flex style={{
              flex: 1,
              overflow: 'hidden',
              background: 'white',
              borderRadius: '20px',
              padding: '12px'
            }}>
              <ListContainer flexDirection={'column'} flex={1} width='100%'>
                <DetailsCoursesProgressAip id={id} journeyName={selectedAip.journeyName} byWeeks={dataThyroidJourney?.providerGetPatientMobileCourses.byWeek.find(x => x.week === selectedAip.weekNumber)} />
              </ListContainer>
            </Flex>
          )}
        </Flex>
      )}
      {!!isMinimized && !!selectedProgram && selectedProgram.value === 'aipProgram' && selectedAip && !loadingThyroidJourney && (
        <Modal
          isOpen={true}
          onBackgroundClick={() => {
            setSelectedAip(undefined);
          }}
          onEscapeKeydown={() => {
            setSelectedAip(undefined);
          }}
        >
          <SpecialModalBackground
            onClick={() => {
              setSelectedAip(undefined);
            }}
          >
            <Box
              style={{
                margin: '0 auto',
                alignSelf: 'center',
                borderRadius: '20px',
                position: 'relative',
                background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                maxWidth: '95vw',
                minWidth: 'initial',
                maxHeight: '95vh',
                minHeight: '95vh',
                overflowY: 'hidden',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto' }}>
                <Flex flex={1} width='100%' overflowY={'hidden'}>
                  {!!selectedAip && (
                    <Flex style={{
                      flex: 1,
                      overflow: 'hidden',
                      padding: '12px'
                    }}>
                      <ListContainer flexDirection={'column'} flex={1} width='100%'>
                        <DetailsCoursesProgressAip id={id} journeyName={selectedAip.journeyName} byWeeks={dataThyroidJourney?.providerGetPatientMobileCourses.byWeek.find(x => x.week === selectedAip.weekNumber)} />
                      </ListContainer>
                    </Flex>
                  )}
                </Flex>
              </Wrapper>
            </Box>
          </SpecialModalBackground>
        </Modal>
      )}
      {!!isMinimized && !!selectedProgram && selectedProgram.value !== 'aipProgram' && selectedSection && !loading && (
        <Modal
          isOpen={true}
          onBackgroundClick={() => {
            setSelectedSection(undefined);
          }}
          onEscapeKeydown={() => {
            setSelectedSection(undefined);
          }}
        >
          <SpecialModalBackground
            onClick={() => {
              setSelectedSection(undefined);
            }}
          >
            <Box
              style={{
                margin: '0 auto',
                alignSelf: 'center',
                borderRadius: '20px',
                position: 'relative',
                background: 'linear-gradient(180deg, #BBD4ED 0%, #FDF5F2 100%)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                maxWidth: '95vw',
                minWidth: 'initial',
                maxHeight: '95vh',
                minHeight: '95vh',
                overflowY: 'hidden',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Wrapper flex={1} flexDirection={'column'} width={'800px'} style={{ overflowY: 'auto' }}>
                <Flex flex={1} width='100%' overflowY={'hidden'}>
                  {!!selectedSection && (
                    <Flex style={{
                      flex: 1,
                      overflow: 'hidden',
                      padding: '12px'
                    }}>
                      <ListContainer flexDirection={'column'} flex={1} width='100%'>
                        <DetailsProgramProgress id={id} plan={selectedSection} />
                      </ListContainer>
                    </Flex>
                  )}
                </Flex>
              </Wrapper>
            </Box>
          </SpecialModalBackground>
        </Modal>
      )}
    </Flex>
  )
}